import { zodResolver } from "@hookform/resolvers/zod";
import { Action } from "@packages/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Ban, CheckCircle2, Loader2, Pencil, Undo2 } from "lucide-react";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { GetLoan200 } from "@/api/financial";
import {
	getLoanQueryOptions,
	useCancelLoan,
	usePayLoanInstallment,
	useSettleLoan,
	useUnpayLoanInstallment,
	useUpdateLoanTerms,
} from "@/api/financial";
import { PageHeader } from "@/components/PageHeader";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import { SubmitButton } from "@/components/ui/submit-button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { centsToDecimalString, toCents } from "@/utils/decimal";
import { formatMoney } from "@/utils/financial";
import { dateInput } from "@/utils/formSchemas";
import {
	InstallmentStatusBadge,
	LoanStatusBadge,
} from "../-components/LoanBadges";
import { LoanForm } from "../-components/LoanForm";
import { toLoanFormValues } from "../-components/loanFormSchema";

type Installment = GetLoan200["installments"][number];

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/loans/$loanId/",
)({
	loader: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			getLoanQueryOptions(params.orgId, params.loanId),
		);
	},
	component: LoanDetailPage,
});

function LoanDetailPage() {
	const { orgId, loanId } = Route.useParams();
	const { t } = useTranslation();
	const { formatDateOnly } = useDateFormatter();
	const canManage = useHasPermission(orgId)(Action.MANAGE_FINANCE);

	const [isEditing, setIsEditing] = useState(false);
	const [payingSeq, setPayingSeq] = useState<number | null>(null);
	const [unpayingSeq, setUnpayingSeq] = useState<number | null>(null);
	const [isSettleOpen, setIsSettleOpen] = useState(false);
	const [isCancelOpen, setIsCancelOpen] = useState(false);

	const { data: loan } = useSuspenseQuery(getLoanQueryOptions(orgId, loanId));

	const updateMutation = useUpdateLoanTerms();
	const payMutation = usePayLoanInstallment();
	const unpayMutation = useUnpayLoanInstallment();
	const settleMutation = useSettleLoan();
	const cancelMutation = useCancelLoan();

	const paidCount = loan.installments.filter((i) => i.status === "PAID").length;
	const canCancel = paidCount === 0;
	const isActive = loan.status === "ACTIVE";
	const payingInstallment = loan.installments.find((i) => i.seq === payingSeq);

	if (isEditing) {
		const editDefaults = toLoanFormValues(loan);
		return (
			<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
				<PageHeader title={t("financial.loans.editTerms")} showBackButton />
				<div className="card bg-base-100 shadow-md">
					<div className="card-body">
						<LoanForm
							orgId={orgId}
							defaultValues={editDefaults}
							isSubmitting={updateMutation.isPending}
							onSubmit={(data) =>
								updateMutation
									.mutateAsync({ organizationId: orgId, loanId, data })
									.then(() => setIsEditing(false))
							}
							onCancel={() => setIsEditing(false)}
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
			<PageHeader title={loan.lender} showBackButton />

			<Card>
				<CardContent className="flex flex-col gap-4 p-5">
					<div className="flex items-start justify-between gap-3">
						<div className="flex flex-wrap items-center gap-2">
							<LoanStatusBadge status={loan.status} />
							<span className="text-sm text-base-content/70">
								{t(`financial.loans.amortizations.${loan.amortization}`)} ·{" "}
								{t(`financial.loans.frequencies.${loan.frequency}`)}
							</span>
						</div>
						<span className="text-xl font-semibold">
							{formatMoney(loan.principal)}
						</span>
					</div>

					{loan.description && (
						<Row
							label={t("financial.loans.form.description")}
							value={loan.description}
						/>
					)}
					<Row
						label={t("financial.loans.form.interestRateAnnual")}
						value={`${(Number(loan.interestRateAnnual) * 100).toFixed(2)}%`}
					/>
					<Row
						label={t("financial.loans.columns.installments")}
						value={String(loan.installmentsCount)}
					/>
					<Row
						label={t("financial.loans.form.graceMonths")}
						value={String(loan.graceMonths)}
					/>
					<Row
						label={t("financial.loans.form.disbursementDate")}
						value={formatDateOnly(new Date(loan.disbursementDate).getTime())}
					/>
					<Row
						label={t("financial.loans.form.firstPaymentDate")}
						value={formatDateOnly(new Date(loan.firstPaymentDate).getTime())}
					/>
					{loan.notes && (
						<Row label={t("financial.loans.form.notes")} value={loan.notes} />
					)}
				</CardContent>
			</Card>

			{canManage && isActive && (
				<div className="flex flex-wrap gap-2">
					<Button variant="outline" onClick={() => setIsEditing(true)}>
						<Pencil className="mr-2 h-4 w-4" />
						{t("financial.loans.editTerms")}
					</Button>
					<Button
						variant="outline"
						onClick={() => setIsSettleOpen(true)}
						disabled={settleMutation.isPending}
					>
						<CheckCircle2 className="mr-2 h-4 w-4" />
						{t("financial.loans.settle.action")}
					</Button>
					{canCancel && (
						<Button
							variant="outline"
							className="text-error"
							onClick={() => setIsCancelOpen(true)}
							disabled={cancelMutation.isPending}
						>
							<Ban className="mr-2 h-4 w-4" />
							{t("financial.loans.cancel.action")}
						</Button>
					)}
				</div>
			)}

			<Card>
				<CardContent className="p-0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-12">#</TableHead>
								<TableHead>{t("financial.loans.schedule.dueDate")}</TableHead>
								<TableHead className="text-right">
									{t("financial.loans.schedule.principal")}
								</TableHead>
								<TableHead className="text-right hidden sm:table-cell">
									{t("financial.loans.schedule.interest")}
								</TableHead>
								<TableHead className="text-right">
									{t("financial.loans.schedule.total")}
								</TableHead>
								<TableHead className="text-right hidden md:table-cell">
									{t("financial.loans.schedule.remainingBalance")}
								</TableHead>
								<TableHead>{t("financial.loans.columns.status")}</TableHead>
								{canManage && <TableHead className="text-right" />}
							</TableRow>
						</TableHeader>
						<TableBody>
							{loan.installments.map((installment) => (
								<TableRow key={installment.id}>
									<TableCell className="text-base-content/60">
										{installment.seq}
									</TableCell>
									<TableCell>
										{formatDateOnly(new Date(installment.dueDate).getTime())}
									</TableCell>
									<TableCell className="text-right">
										{formatMoney(installment.principalAmount)}
									</TableCell>
									<TableCell className="text-right hidden sm:table-cell">
										{formatMoney(installment.interestAmount)}
									</TableCell>
									<TableCell className="text-right font-medium">
										{formatMoney(installment.totalAmount)}
									</TableCell>
									<TableCell className="text-right hidden md:table-cell text-base-content/70">
										{formatMoney(installment.remainingBalance)}
									</TableCell>
									<TableCell>
										<InstallmentStatusBadge status={installment.status} />
									</TableCell>
									{canManage && (
										<TableCell className="text-right">
											{isActive && installment.status === "SCHEDULED" && (
												<Button
													size="sm"
													variant="outline"
													onClick={() => setPayingSeq(installment.seq)}
												>
													{t("financial.loans.pay.action")}
												</Button>
											)}
											{installment.status === "PAID" && (
												<Button
													size="sm"
													variant="ghost"
													onClick={() => setUnpayingSeq(installment.seq)}
												>
													<Undo2 className="mr-1 h-3 w-3" />
													{t("financial.loans.unpay.action")}
												</Button>
											)}
										</TableCell>
									)}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			{payingInstallment && (
				<PayInstallmentDialog
					installment={payingInstallment}
					isSubmitting={payMutation.isPending}
					onClose={() => setPayingSeq(null)}
					onConfirm={(data) =>
						payMutation
							.mutateAsync({
								organizationId: orgId,
								loanId,
								seq: String(payingInstallment.seq),
								data,
							})
							.then(() => setPayingSeq(null))
					}
				/>
			)}

			<AlertDialog
				open={unpayingSeq !== null}
				onOpenChange={(open) => !open && setUnpayingSeq(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.loans.unpay.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.loans.unpay.confirm")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={unpayMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (unpayingSeq === null) return;
								return unpayMutation
									.mutateAsync({
										organizationId: orgId,
										loanId,
										seq: String(unpayingSeq),
									})
									.then(() => setUnpayingSeq(null));
							}}
							disabled={unpayMutation.isPending}
						>
							{unpayMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("financial.loans.unpay.action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={isSettleOpen} onOpenChange={setIsSettleOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.loans.settle.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.loans.settle.confirm")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={settleMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								settleMutation
									.mutateAsync({ organizationId: orgId, loanId })
									.then(() => setIsSettleOpen(false))
							}
							disabled={settleMutation.isPending}
						>
							{settleMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("financial.loans.settle.action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.loans.cancel.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.loans.cancel.confirm")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={cancelMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								cancelMutation
									.mutateAsync({ organizationId: orgId, loanId })
									.then(() => setIsCancelOpen(false))
							}
							disabled={cancelMutation.isPending}
						>
							{cancelMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("financial.loans.cancel.action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

function Row({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-start justify-between gap-3 border-b border-base-200 pb-2 text-sm last:border-0">
			<span className="text-base-content/60">{label}</span>
			<span className="text-right font-medium">{value}</span>
		</div>
	);
}

/** Optional money string — empty allowed; non-empty must parse (validate-only). */
const optionalAmountString = z
	.string()
	.refine((v) => v.trim() === "" || toCents(v) !== undefined, {
		message: "forms.validation.invalidNumber",
	});

const payInstallmentSchema = z.object({
	paidDate: dateInput(),
	paidPrincipal: optionalAmountString,
	paidInterest: optionalAmountString,
});
type PayInstallmentValues = z.infer<typeof payInstallmentSchema>;

/** Canonical decimal string for the wire, or omit when the field is blank. */
const optionalCanonical = (value: string): string | undefined => {
	if (value.trim() === "") return undefined;
	const cents = toCents(value);
	return cents === undefined ? undefined : centsToDecimalString(cents);
};

function PayInstallmentDialog({
	installment,
	isSubmitting,
	onConfirm,
	onClose,
}: {
	installment: Installment;
	isSubmitting: boolean;
	onConfirm: (data: {
		paidDate: string;
		paidPrincipal?: string;
		paidInterest?: string;
	}) => Promise<unknown> | undefined;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { formatForDateInput } = useDateFormatter();
	const formId = useId();

	const form = useForm<PayInstallmentValues>({
		resolver: zodResolver(payInstallmentSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: {
			paidDate: formatForDateInput(new Date(installment.dueDate).getTime()),
			paidPrincipal: installment.principalAmount,
			paidInterest: installment.interestAmount,
		},
	});

	const guardedSubmit = useGuardedSubmit(form, (values) => {
		const paidPrincipal = optionalCanonical(values.paidPrincipal);
		const paidInterest = optionalCanonical(values.paidInterest);
		return onConfirm({
			paidDate: new Date(values.paidDate).toISOString(),
			...(paidPrincipal ? { paidPrincipal } : {}),
			...(paidInterest ? { paidInterest } : {}),
		});
	});

	return (
		<ResponsiveFormDialog
			open
			onOpenChange={(open) => !open && onClose()}
			title={t("financial.loans.pay.title", { seq: installment.seq })}
			description={t("financial.loans.pay.description")}
			pending={isSubmitting}
			footer={
				<>
					<Button
						type="button"
						variant="outline"
						disabled={isSubmitting}
						onClick={onClose}
					>
						{t("common.cancel")}
					</Button>
					<SubmitButton form={formId} pending={isSubmitting}>
						{t("financial.loans.pay.confirm")}
					</SubmitButton>
				</>
			}
		>
			<Form {...form}>
				<form id={formId} className="space-y-4 py-2" onSubmit={guardedSubmit}>
					<FormField
						control={form.control}
						name="paidDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("financial.loans.pay.paidDate")}
								</FormLabel>
								<FormControl>
									<Input type="date" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<div className="grid gap-4 sm:grid-cols-2">
						<FormField
							control={form.control}
							name="paidPrincipal"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("financial.loans.pay.paidPrincipal")}
									</FormLabel>
									<FormControl>
										<NumberField
											value={field.value}
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
											ref={field.ref}
											suffix="R$"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="paidInterest"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t("financial.loans.pay.paidInterest")}</FormLabel>
									<FormControl>
										<NumberField
											value={field.value}
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
											ref={field.ref}
											suffix="R$"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<FormRootMessage />
				</form>
			</Form>
		</ResponsiveFormDialog>
	);
}
